import { HttpHeaders, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ApiProviderService } from 'src/app/services/api-provider.service';
import { LoginSettingsResponse } from 'src/app/modules/shared/model/loginSettingsResponse.model';
import { RecaptchaSettings } from 'src/app/modules/shared/model/recaptchaSettings.model';
import { HttpErrorHandler } from 'src/app/modules/shared/services/error-handler.service';
import { StringKeyValue } from '../model/stringKeyValue.model';
import { ApiResponse } from 'src/app/model/apiResponse.model';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/app.state';
import { selectAppSettings } from 'src/app/state/selectors/app-settings.selectors';
import { addAppSettings } from 'src/app/state/actions/app-settings.actions';

@Injectable({
  providedIn: 'root'
})
export class SettingService {

  headers: HttpHeaders;
  apiServer: string;
  loginSettings: LoginSettingsResponse;
  recaptcha: RecaptchaSettings;
  isLicenseRequiredForAccount: boolean = null;
  enableCustomizationPerPage: boolean = null;
  isACASSOEnabledForLogin: boolean = null;
  dateFormat: string = '';

  constructor(private http: HttpClient, private apiRef: ApiProviderService, private errorHandler: HttpErrorHandler,
     private store: Store<AppState>) {
    this.headers = new HttpHeaders();
    this.headers.append('Content-Type', 'application/json');
    this.headers.append('Accept', 'application/json');
    this.apiServer = 'api/Settings/';
  }

  getLoginSettings(): Observable<LoginSettingsResponse> {
    if (this.loginSettings) {
      return of(this.loginSettings);
    }

    return this.http.get<LoginSettingsResponse>(this.apiRef.getApiUrl(`${this.apiServer}GetLoginSettings`))
      .pipe(map((result) => {
        this.loginSettings = result as LoginSettingsResponse;
        return this.loginSettings;
      }))
      .pipe(catchError(this.errorHandler.handleError));

  }

  isContactAddressEnabled(): Observable<boolean> {
    return this.http.get(this.apiRef.getApiUrl(`${this.apiServer}GetIsContactAddressSetting`)).pipe(map((result) => result as boolean));
  }

  isRegistrationEnabled(): Observable<boolean> {
    return this.http.get(this.apiRef.getApiUrl(`${this.apiServer}GetRegistrationEnabledSetting`)).pipe(map((result) => result as boolean));
  }

  getRecaptchaSettings(): Observable<RecaptchaSettings> {
    if (this.recaptcha) {
      return of(this.recaptcha);
    }
    return this.http.get<RecaptchaSettings>(this.apiRef.getApiUrl(`${this.apiServer}GetRecaptchaInfo`))
      .pipe(map((result) => {
        this.recaptcha = result as RecaptchaSettings;
        return this.recaptcha;
      }))
      .pipe(catchError(this.errorHandler.handleError));
  }

  isXAPOAgency(): Observable<boolean> {
    return this.http.get<boolean>(this.apiRef.getApiUrl(`${this.apiServer}IsXAPOAgency`))
      .pipe(map((result) => result as boolean))
      .pipe(catchError(this.errorHandler.handleError));
  }

  getLicenseTypes(): Observable<StringKeyValue[]> {
    return this.http.get<ApiResponse>(this.apiRef.getApiUrl(`${this.apiServer}licenseTypes`))
      .pipe(map((res) => res.result as StringKeyValue[]));
  }

  isLicenseRequiredForRegistration(): Observable<boolean> {
    if (this.isLicenseRequiredForAccount == null) {
      return this.http.get<ApiResponse>(this.apiRef.getApiUrl(`${this.apiServer}configValue`,
      [{Key: 'key', Value: 'registrationIsLicenseReq'}]))
      .pipe(map((res) => {
        this.setConfigCacheValues(res);
        return this.isLicenseRequiredForAccount;
      }));
    } else {
      return of(this.isLicenseRequiredForAccount);
    }
  }

  isEnableCustomizationPerPage(): Observable<boolean> {
    if (this.enableCustomizationPerPage == null) {
      return this.http.get<ApiResponse>(this.apiRef.getApiUrl(`${this.apiServer}configValue`,
        [{ Key: 'key', Value: 'customPageSettings' }]))
        .pipe(map((res) => {
          this.setConfigCacheValues(res);
          return this.enableCustomizationPerPage;
        }))
        .pipe(catchError(this.errorHandler.handleError));
    } else {
      return of(this.enableCustomizationPerPage);
    }
  }

  isACASSOEnabled(): Observable<boolean> {
    if (this.isACASSOEnabledForLogin == null) {
      return this.http.get<ApiResponse>(this.apiRef.getApiUrl(`${this.apiServer}configValue`))
        .pipe(map((res) => {
          this.setConfigCacheValues(res);
          return this.isACASSOEnabledForLogin;
        }))
        .pipe(catchError(this.errorHandler.handleError));
    }
    else {
      return of(this.isACASSOEnabledForLogin);
    }
  }

  isDateFormatConfigured(): Observable<boolean> {
    if (this.dateFormat == '') {
      return this.http.get<ApiResponse>(this.apiRef.getApiUrl(`${this.apiServer}configValue`))
        .pipe(map((res) => {
          this.setConfigCacheValues(res);
          return this.dateFormat != '';
        }))
        .pipe(catchError(this.errorHandler.handleError));
    }
    else {
      return of(this.dateFormat != '');
    }
  }

  setConfigCacheValues(res: ApiResponse) {
    this.isLicenseRequiredForAccount = res.result?.isLicenseRequiredForRegistration as boolean;
    this.enableCustomizationPerPage = res.result?.isEnableCustomizationPerPage as boolean;
    this.isACASSOEnabledForLogin = res.result?.isACASSOEnabled as boolean;
    this.dateFormat = res.result?.dateFormat as string;
  }
}
